/**
 * Content hooks — fetch articles, journeys, and prompts from the API,
 * with localStorage caching and a fallback function for offline/error cases.
 *
 * Pattern:
 *   const { data, loading, error } = useContent(
 *     'prompts:grounding',
 *     () => fetch('/api/content/prompts/grounding').then(r => r.json()),
 *     fallbackData
 *   );
 *
 * Or use the typed helpers:
 *   const { data: prompts } = useGroundingPrompts(FALLBACK_GROUNDING_STEPS);
 */

import { useState, useEffect, useRef } from 'react';

const CACHE_PREFIX = 'cw_content_cache_';
const CACHE_TTL_MS = 1000 * 60 * 60 * 24; // 24 hours

interface CachedEntry<T> {
  data: T;
  cachedAt: number;
}

function readCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: CachedEntry<T> = JSON.parse(raw);
    if (Date.now() - entry.cachedAt > CACHE_TTL_MS) return null;
    return entry.data;
  } catch {
    return null;
  }
}

function writeCache<T>(key: string, data: T): void {
  try {
    const entry: CachedEntry<T> = { data, cachedAt: Date.now() };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // localStorage full or unavailable — ignore
  }
}

/**
 * Generic content fetching hook with cache-first, network-update, fallback-on-error.
 *
 * @param cacheKey  Stable key for localStorage (e.g. 'prompts:grounding')
 * @param fetcher   Function returning a promise with the data
 * @param fallback  Optional fallback data to use if fetch fails AND no cache exists
 */
export function useContent<T>(
  cacheKey: string,
  fetcher: () => Promise<T>,
  fallback?: T,
): { data: T | null; loading: boolean; error: Error | null; refresh: () => void } {
  const [data, setData] = useState<T | null>(() => readCache<T>(cacheKey) ?? fallback ?? null);
  const [loading, setLoading] = useState(data === null);
  const [error, setError] = useState<Error | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetcher()
      .then(fresh => {
        if (cancelled || !mountedRef.current) return;
        setData(fresh);
        writeCache(cacheKey, fresh);
        setError(null);
      })
      .catch((err: Error) => {
        if (cancelled || !mountedRef.current) return;
        setError(err);
        // Fall back to cached or static data — already set in state
      })
      .finally(() => {
        if (cancelled || !mountedRef.current) return;
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [cacheKey, refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    data,
    loading,
    error,
    refresh: () => setRefreshTick(t => t + 1),
  };
}

// ── Typed content models ────────────────────────────────────────────
// (ContentArticle interface lives further down with the article hook —
//  it's the rich content_articles schema.)

export interface ContentJourneyPhase {
  id: string;
  title: string;
  resources: string[];
}

export interface ContentJourney {
  id: number;
  slug: string;
  title: string;
  description: string;
  phases: ContentJourneyPhase[];
  sort_order: number;
}

export interface ContentPrompt {
  id: number;
  category: string;
  key: string;
  label: string;
  body: string;
  metadata: Record<string, unknown>;
  sort_order: number;
}

export interface ContentJourneyResource {
  id: number;
  journey_slug: string;
  phase_key: string;
  resource_id: string;
  icon: string;
  title: string;
  description: string;
  resource_type: 'checklist' | 'tip' | 'script' | 'reflection';
  items: string[];
  sort_order: number;
}

export interface ContentJourneyResourcesBySlug {
  slug: string;
  phases: Record<string, ContentJourneyResource[]>;
}

/** All journey resources grouped by journey slug, then phase key. */
export type ContentAllJourneyResources = Record<string, Record<string, ContentJourneyResource[]>>;

const API = '/api/content';

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API}${path}`);
  if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
  return res.json() as Promise<T>;
}

// ── Typed convenience hooks ─────────────────────────────────────────
// (useArticles lives further down with the ContentArticle interface.)

export function useJourneys(fallback?: ContentJourney[]) {
  return useContent<ContentJourney[]>(
    'journeys',
    () => fetchJson<ContentJourney[]>('/journeys'),
    fallback,
  );
}

export function useJourney(slug: string, fallback?: ContentJourney) {
  return useContent<ContentJourney>(
    `journey:${slug}`,
    () => fetchJson<ContentJourney>(`/journeys/${slug}`),
    fallback,
  );
}

export function usePrompts(category: string, fallback?: ContentPrompt[]) {
  return useContent<ContentPrompt[]>(
    `prompts:${category}`,
    () => fetchJson<ContentPrompt[]>(`/prompts/${category}`),
    fallback,
  );
}

// ── Article hook (rich ED/articles content) ───────────────────────────

export interface ContentArticle {
  id: number;
  slug: string;
  content_type: string;
  title: string;
  summary: string;
  body: string;
  script_lines: string[];
  journey: string;
  phase: string[];
  module: string[];
  stigma_vital_signs: string[];
  tags: string[];
  intensity: number;
  delivery_mode: string[];
  estimated_time_minutes: number;
  notes_for_youth: string | null;
  age_at_event: number | null;
  self_described_identities: string[];
  content_warnings: string[];
  gif: string | null;
  sort_order: number;
}

export function useArticles(fallback?: ContentArticle[]) {
  return useContent<ContentArticle[]>(
    'articles',
    () => fetchJson<ContentArticle[]>('/articles'),
    fallback,
  );
}

/** Fetch all journey resources for a specific journey, grouped by phase. */
export function useJourneyResources(slug: string, fallback?: ContentJourneyResourcesBySlug) {
  return useContent<ContentJourneyResourcesBySlug>(
    `journey-resources:${slug}`,
    () => fetchJson<ContentJourneyResourcesBySlug>(`/journey-resources/${slug}`),
    fallback,
  );
}

/** Fetch ALL journey resources at once (journey → phase → resources). */
export function useAllJourneyResources(fallback?: ContentAllJourneyResources) {
  return useContent<ContentAllJourneyResources>(
    'journey-resources:all',
    () => fetchJson<ContentAllJourneyResources>('/journey-resources'),
    fallback,
  );
}

/** Convenience: lookup a specific prompt within a category by key. */
export function findPrompt(prompts: ContentPrompt[] | null, key: string): ContentPrompt | undefined {
  return prompts?.find(p => p.key === key);
}

/** Convenience: get the body of a specific prompt, or a fallback string. */
export function promptBody(prompts: ContentPrompt[] | null, key: string, fallback: string): string {
  return findPrompt(prompts, key)?.body ?? fallback;
}

/** Clear all content cache entries — useful after admin edits. */
export function clearContentCache(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) keys.push(k);
    }
    keys.forEach(k => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}
